import { Box, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { IStatisticalLogReportFilters } from "./Filters";
import { useLanguageContext } from "../../../../../contexts/LanguageContext";
import { useEffect, useMemo, useState } from "react";
import { IStatisticalLog } from '../../../../../interfaces/IRoomLog';
import RoomLogService from '../../../../../services/RoomLogService';
import FormHelper from '../../../../../helpers/FormHelper';
import DateTimeHelper from '../../../../../helpers/DateTimeHelper';
import DefaultConstants from "../../../../../data/Constants";
import IAccessProfile from "../../../../../interfaces/IAccessProfile";
import { MeetingRoom, People, Schedule, ThumbDownAlt } from "@mui/icons-material";
import NumberHelper from "../../../../../helpers/NumberHelper";
import StringHelper from "../../../../../helpers/StringHelper";


const StatisticalLogContent: React.FunctionComponent<IStatisticalLogReportFilters> = (filters) => {
    const { translate } = useLanguageContext();

    const [data, setData] = useState<IStatisticalLog[]>([]);


    useEffect(() => {
        let formData = FormHelper.Create(undefined);
        
        Object.entries(filters).forEach(([key, value]) => {
            if (key === 'enterprise') {
                key = 'enterpriseId';
                value = value.id;
            } else if (key === 'accessProfile') {
                value = value.map((v: IAccessProfile) => v.id).join(',');
            }

            formData = FormHelper.Add(formData, key, value);
        });

        RoomLogService.GetStatisticalLog(formData).then(resp => {
            setData(resp.data.statisticalLog as IStatisticalLog[]);
        }).catch(err => {
            setData([]);
        });

        // eslint-disable-next-line
    }, []);


    const renderFilterFieldset = useMemo(() => {
        return <TableCell className='reportpreview-paper-content-fieldset'>
            <Typography variant='caption' className='reportpreview-paper-content-fieldset-label'>
                {translate('filters')}
            </Typography>

            <Grid container>
                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('startDate')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {DateTimeHelper.Format(filters.startDate, 'DD/MM/YYYY')}
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('endDate')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {DateTimeHelper.Format(filters.endDate, 'DD/MM/YYYY')}
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('accessProfile')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {
                            filters.accessProfile.length === 0 ? (
                                translate('all')
                            ) : (
                                filters.accessProfile.map(a => a.description).join(', ')
                            )
                        }
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('_hideZeroedUsers')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {translate(filters.hideZeroedUsers ? 'yes' : 'no')}
                    </Typography>
                </Grid>
            </Grid>
        </TableCell>
    }, [filters, translate]);


    const averageResults = useMemo(() => {
        let executedRooms = 0;
        let executionTime = 0;
        let reproved = 0;

        data.forEach(row => {
            executedRooms += +row.executedRooms;
            executionTime += +(row.executionTime ?? 0);
            reproved += +row.reprovedOnInspection;
        });
        
        return {
            executedRooms: Math.ceil(executedRooms / data.length),
            executionTime: NumberHelper.Average(executionTime, executedRooms),
            reproved: Math.ceil(reproved / data.length)
        };
    }, [data]);

    
    return <>
        <TableRow>
            {renderFilterFieldset}
        </TableRow>

        <TableRow>
            <TableCell className='border-none' padding='none'>
                {
                    data.length > 0 ? (
                        <>
                            <Table className='data-table' sx={{ marginY: DefaultConstants.gridRowSpacing }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate('user')}</TableCell>
                                        <TableCell>{translate('accessProfile')}</TableCell>
                                        <TableCell>{translate('executed')}</TableCell>
                                        <TableCell>{translate('totalTime')}</TableCell>
                                        <TableCell>{translate('reproved')}</TableCell>
                                        <TableCell>{translate('approval')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                
                                <TableBody className='tbody-striped'>
                                    {
                                        data.map((row, index) => {
                                            const averageTime = Math.ceil(NumberHelper.Average(+row.executionTime, +row.executedRooms));

                                            let averageReprovedOnInspection = 0;
                                            let averageApprovedOnInspection = 0;

                                            if (+row.sentForInspection !== 0) {
                                                averageReprovedOnInspection = row.reprovedOnInspection / row.sentForInspection * 100;
                                                averageApprovedOnInspection = 100 - averageReprovedOnInspection;
                                            }
                                            
                                            return <TableRow key={index}>
                                                <TableCell>
                                                    <Typography className='text-title'> {row.user.login} </Typography>
                                                    <Typography className='text-subtitle'> {row.user.fullName} </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title fontweight-normal'> {row.user.accessProfile.description} </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title'> {row.executedRooms} {translate('rooms')} </Typography>
                                                    <Typography className='text-subtitle'> {row.distinctRoomsExecuted} {translate('differents').toLowerCase()} </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title'> {DateTimeHelper.ToElapsedTime(row.executionTime)} </Typography>
                                                    <Typography className='text-subtitle'>
                                                        {
                                                            translate('_statisticalLogAverageOfPerRoom')
                                                                .replace('{value}', DateTimeHelper.ToElapsedTime(averageTime))
                                                        } 
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title color-red'>{row.reprovedOnInspection} {translate('rooms')}</Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title color-green'>
                                                        {averageApprovedOnInspection.toFixed(1)}
                                                        <Typography component='span' className='fontsize-9' marginLeft={0.4}>%</Typography> 
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        })
                                    }
                                </TableBody>
                            </Table>

                            {/* Summary */}
                            <Table className='page-break show-only-in-print'>
                                {renderFilterFieldset}
                            </Table>

                            <Table className='data-table' sx={{ marginTop: DefaultConstants.gridRowSpacing }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell colSpan={3}>{translate('summary')}</TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell />
                                        <TableCell>{translate('total')}</TableCell>
                                        <TableCell>{translate('average')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                
                                <TableBody className='tbody-striped'>
                                    <TableRow>
                                        <TableCell className='color-primary display-flex alignitems-center fontweight-600 gap-10'>
                                            <People className='fontsize-20' />
                                            {translate('usersFound')}
                                        </TableCell>
                                        <TableCell className='fontweight-600'>{data.length}</TableCell>
                                        <TableCell className='fontweight-600'> - </TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell className='display-flex alignitems-center fontweight-600 gap-10'>
                                            <MeetingRoom className='fontsize-20' />
                                            {translate('roomsExecuted')}
                                        </TableCell>
                                        <TableCell className='fontweight-600'>
                                            {
                                                data.reduce((prev, curr) => {
                                                    return prev + (+curr.executedRooms);
                                                }, 0)
                                            }
                                        </TableCell>
                                        <TableCell className='fontweight-600'>
                                            {averageResults.executedRooms}
                                            {' '}
                                            {translate('perUser').toLowerCase()}
                                        </TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell className='display-flex alignitems-center fontweight-600 gap-10'>
                                            <Schedule className='fontsize-20' />
                                            {translate('totalTime')}
                                        </TableCell>
                                        <TableCell className='fontweight-600'>
                                            {
                                                DateTimeHelper.ToElapsedTime(data.reduce((prev, curr) => {
                                                    return prev + (+curr.executionTime);
                                                }, 0))
                                            }
                                        </TableCell>
                                        <TableCell className='fontweight-600'>
                                            {DateTimeHelper.ToElapsedTime(Math.ceil(averageResults.executionTime))}
                                            {' '}
                                            {StringHelper.FirstLetterLowerCase(translate('perRoom'))}
                                        </TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell className='color-red display-flex alignitems-center fontweight-600 gap-10'>
                                            <ThumbDownAlt className='fontsize-20' />
                                            {translate('roomsReproved')}
                                        </TableCell>
                                        <TableCell className='fontweight-600'>
                                            {
                                                data.reduce((prev, curr) => {
                                                    return prev + (+curr.reprovedOnInspection);
                                                }, 0)
                                            }
                                        </TableCell>
                                        <TableCell className='fontweight-600'>
                                            {averageResults.reproved}
                                            {' '}
                                            {translate('perUser').toLowerCase()}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </>
                    ) : (
                        <Box className='reportpreview-paper-content-notfound'>
                            <Typography variant='body1'>{translate('_statisticalLogEmptyUserList')}</Typography>
                        </Box>
                    )
                }
            </TableCell>
        </TableRow>
    </>;
}

export default StatisticalLogContent;